import { ICrudLocale } from '@app/i18n/interface';

export const CATALOG_SYNC_MANAGEMENT_LOCALE_EN: ICrudLocale = {
  CATALOG_DESTINATION: {
    LABEL: {
      SINGULAR: 'Catalogue destination',
      PLURAL: 'Catalogue destinations',
    },
    TITLE: 'Destinations that are synced',
    SUBTITLE: 'Editing this list spends no Hotelbeds quota.',
    EMPTY: 'There are no destinations in the list yet.',
    FIELD: {
      CODE: 'Code',
      NAME: 'Name',
      ACTIVE: 'Synced',
      ORDER: 'Order',
    },
    SOURCE: {
      DATABASE: 'The sync uses this list',
      DATABASE_DESC: 'Active destinations are synced, in this order.',
      ENVIRONMENT: 'The server is still in charge',
      ENVIRONMENT_DESC:
        'For now the destinations configured on the server are synced ({{codes}}). As soon as you activate one here, this list takes over.',
      ENVIRONMENT_FULL_DESC:
        'For now the whole Hotelbeds catalogue is synced, because the server does not limit destinations. As soon as you activate one here, this list takes over.',
    },
    IMPORT: {
      DESC: 'The server has {{codes}} configured and they are not here yet.',
      ACTION: 'Bring the server ones',
    },
    ACTION: {
      ADD: 'Add destination',
      REMOVE: 'Remove',
      MOVE_UP: 'Move {{code}} up in the order',
      MOVE_DOWN: 'Move {{code}} down in the order',
    },
    CONFIRM_REMOVE: {
      TITLE: 'Remove {{code}} from the list?',
      DESC: 'Its hotels are not deleted: they stay on the website but stop being updated. To pause it, deactivate it instead.',
      CONFIRM: 'Remove {{code}}',
      CANCEL: 'Keep it',
    },
    HINT: {
      ORDER:
        'Order matters: every sync has a request ceiling and spends it top to bottom.',
      REMOVE:
        'Removing a destination does not delete its hotels, but stops updating them.',
    },
    VALIDATION: {
      CODE_PATTERN: '2 to 8 letters or digits, no spaces (MAD, MIA).',
    },
    ERROR: {
      SAVE: 'We could not save the change. Please try again.',
    },
  },
  CATALOG_SYNC: {
    LABEL: { SINGULAR: 'Catalogue', PLURAL: 'Catalogue' },
    FIELD: {},
    TITLE: 'Catalogue',
    SUBTITLE:
      'Hotelbeds content stored in IBSOA: hotels, descriptions and images. The website reads this copy; it does not query Hotelbeds on every search.',
    EMPTY: 'It has never been synced yet.',
    STORED: {
      TITLE: 'What is stored',
      COMPUTED_AT: 'Computed on {{date}}',
      NOT_COMPUTED:
        'Not computed yet. Computed when each hotel sync finishes and when coverage is measured; opening this page never scans the catalogue.',
      UNMAINTAINED:
        'There are also {{hotels}} stored hotels from another {{destinations}} destinations that are not synced: they stay on the website but are not updated.',
    },
    COUNTS: {
      HOTELS: 'Hotels',
      DESTINATIONS: 'Destinations',
      COUNTRIES: 'Countries',
      CHAINS: 'Chains',
      WITH_IMAGES: 'Hotels with an image',
      ACCOMMODATION_TYPES: 'Accommodation types',
      BOARD_TYPES: 'Board types',
      FACILITY_TYPES: 'Facilities',
      MANAGED: 'Listed destinations being synced: {{count}}.',
      RETIRED: 'Retired because Hotelbeds no longer has them: {{count}}.',
      TRANSLATIONS: 'Translated hotel pages: {{count}}.',
      UNAVAILABLE: 'We could not load the counts. Please try again.',
    },
    COVERAGE: {
      TITLE: 'Mapping coverage',
      THRESHOLDS:
        'Hotelbeds minimums: {{gold}} % for G&D (Gold & Diamond) hotels and {{regular}} % for the rest.',
      IDENTIFIABLE: {
        TITLE: 'Identifiable hotels',
        DESC: 'Active hotels with name, address, city and country: the ones Hotelbeds can match against its catalogue. Computed in the background when each hotel sync finishes and when coverage is measured.',
      },
      PROVIDER: {
        TITLE: 'Coverage against Hotelbeds',
        DESC: 'Of the hotels Hotelbeds has in the destinations we sell, how many we have active. Measured by the «Coverage against Hotelbeds» job.',
      },
      VERDICT: {
        MEETS_GOLD_DIAMOND: 'Meets both minimums',
        MEETS_REGULAR: 'Meets 90 %, not 95 %',
        BELOW_REGULAR: 'Below the minimum',
        NOT_MEASURED: 'Not measured',
      },
      NOT_MEASURED: {
        identifiable: 'There are no active hotels to measure.',
        provider:
          'Not measured yet. Start it with «Run now» on «Coverage against Hotelbeds».',
      },
      MATCHED: '{{matched}} of {{total}} hotels.',
      BELOW: 'Below {{gold}} %: {{list}}.',
      EXTRA:
        'Ours no longer in Hotelbeds: {{count}}. The next full pass will retire them.',
      MEASURED_AT: 'Computed on {{date}}.',
      NOT_COMPUTED:
        'Not computed yet. It is computed when the next hotel sync finishes or when «Coverage against Hotelbeds» is started.',
      SCOPE: 'Measured destinations: {{list}}.',
      SCOPE_FULL: 'The whole catalogue is measured.',
      GD_NOTE:
        'Hotelbeds does not tell us which hotels are G&D, so each figure is compared with both minimums.',
      UNAVAILABLE: 'We could not load the coverage. Please try again.',
    },
    JOB: {
      HOTEL_CATALOG: {
        NAME: 'Hotels and content',
        DESC: 'Hotels, descriptions, images and facilities of the listed destinations.',
      },
      DICTIONARIES: {
        NAME: 'Dictionaries',
        DESC: 'Names of facilities, board types and accommodation types. Costs few requests.',
      },
      COVERAGE: {
        NAME: 'Coverage against Hotelbeds',
        DESC: 'Compares the Hotelbeds hotel list with ours. Read only: it does not change the catalogue.',
      },
    },
    JOBS: {
      TITLE: 'Sync jobs',
      SUBTITLE:
        'Every job can be started manually. What is not scheduled only runs when you start it.',
      NEXT: 'Runs on its own; next time on {{date}}.',
      SCHEDULED: 'Runs on its own every day.',
      RUNS_WITH: 'Also runs at the start of «{{job}}».',
      MANUAL_ONLY: 'Only when you start it.',
      NEVER: 'It has not run yet.',
      PROCESSED: 'processed: {{count}}',
      RUN_NOW_LABEL: 'Run now: {{job}}',
      BLOCK: {
        RUNNING: 'It is running.',
        COOLDOWN: 'It can be started again from {{date}}.',
      },
      LAUNCHED: {
        TITLE: 'Started: {{job}}',
        DESC: 'It runs in the background. This page refreshes on its own until it finishes.',
      },
      ERROR: {
        COOLDOWN:
          'This job was started moments ago. Wait until the time shown before repeating it.',
        ALREADY_RUNNING: 'This job is already running.',
        GENERIC: 'We could not start the job. Please try again.',
      },
      UNAVAILABLE: 'We could not load the jobs. Please try again.',
      NOTE: 'Every run has a single status. If it fails, the reason shows in the history and the next one is scheduled on its own. Content is never requested from Hotelbeds during a booking.',
    },
    HISTORY: {
      TITLE: 'Sync history',
      SUBTITLE: 'Most recent first. Times in your local time.',
      FILTER: 'Filter the history',
      JOB: 'Job',
      ALL_JOBS: 'All jobs',
      STARTED: 'Start',
      DURATION: 'Duration',
      PROCESSED: 'Processed',
      REQUESTS: 'Requests',
      RESULT: 'Result',
      BY: 'Manual, {{name}}',
      NO_DATA: 'No runs recorded yet.',
      NO_DATA_FILTER: 'This job has not run yet.',
    },
    RUN_STATUS: {
      RUNNING: 'Running',
      SUCCESS: 'Successful',
      PARTIAL: 'With failures',
      FAILED: 'Failed',
    },
    TRIGGER_SHORT: { SCHEDULED: 'Scheduled', MANUAL: 'Manual' },
    RUN: {
      TITLE: 'Hotel sync: last run',
      STATE: 'Status',
      STARTED_AT: 'Started',
      DURATION: 'Duration',
      DURATION_VALUE: '{{minutes}} min {{seconds}} s',
      RESULT: 'Result',
      RESULT_VALUE:
        '{{hotels}} hotels updated; {{ok}} destinations fine and {{failed}} failed.',
      LAST_SUCCESS: 'Last successful sync',
      NEVER: 'Never',
      NEXT: 'Next sync',
      NEXT_INCREMENTAL: 'Only changes since {{date}}',
      NEXT_FULL: 'Full: all content of the destinations',
      DESTINATIONS: 'Included destinations',
      FULL_CATALOG: 'The whole Hotelbeds catalogue',
      ERRORS: 'Why it did not go well',
      NOTES: 'Notes from this sync',
      TECHNICAL: 'See the technical message from Hotelbeds',
      NOTE: 'It syncs on its own every day and updates hotels, descriptions and images of the listed destinations. Times in your local time.',
    },
    STATE: {
      IDLE: 'Never run',
      RUNNING: 'Running',
      SUCCESS: 'Successful',
      PARTIAL: 'With failures',
      FAILED: 'Failed',
    },
    STATE_DESC: {
      IDLE: 'It has never run yet.',
      RUNNING: 'It is running now.',
      SUCCESS: 'Finished without errors,',
      PARTIAL: 'Finished, but some destinations failed,',
      FAILED: 'Did not finish,',
    },
    TRIGGER: {
      SCHEDULED: 'started automatically.',
      MANUAL: 'started manually.',
    },
    ACTION: {
      TRIGGER: 'Run now',
      TRIGGERING: 'Starting…',
    },
    COST: {
      DESC: 'Every run spends requests from the daily Hotelbeds quota (50,000 a day for the whole team). To avoid repeating it by mistake, the same job cannot be started again for a while.',
    },
    POLL_TIMEOUT: {
      DESC: 'The sync is still running. Come back later to see how it finished.',
    },
    ERROR: {
      TITLE: 'The action could not be completed',
      LOAD: 'We could not load the catalogue status. Please try again.',
    },
  },
};
